import { ENDPOINTS, getApiUrl } from "@/common";
import { KeyTokenName } from "@/constants";

let currentAudio: HTMLAudioElement | null = null;
let abortController: AbortController | null = null;
let currentRequestId = 0; // Định danh duy nhất cho mỗi lượt phát

export const stopAllAudio = () => {
  // Tăng ID để vô hiệu hóa các callback của lượt phát cũ ngay lập tức
  currentRequestId++;

  if (abortController) {
    abortController.abort();
    abortController = null;
  }

  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }

  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

/**
 * Ưu tiên phát TTS qua Backend Proxy (Google API).
 * Chỉ sử dụng Web Speech API (Native) làm phương án dự phòng khi API lỗi.
 */
export const onPlayReadStr = (str: string, onNext?: () => void, _lang: string = "vi-VN", speed?: number) => {
  if (typeof window === "undefined") return;

  stopAllAudio(); // Dừng lượt cũ và tăng requestId

  // Lưu lại ID của lượt phát HIỆN TẠI
  const requestId = currentRequestId;

  // const storedRate = localStorage.getItem("speed_audio");
  const rate = speed !== undefined ? speed : 1;

  // Hàm kiểm tra xem lượt phát này còn hiệu lực không
  const isActive = () => requestId === currentRequestId;

  const fallbackNative = () => {
    // Nếu ID đã thay đổi (có lượt hover mới), tuyệt đối không chạy Native của lượt cũ
    if (!isActive()) return;

    const synth = window.speechSynthesis;
    if (!synth) {
      onNext?.();
      return;
    }

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(str);
    utterance.lang = _lang;
    utterance.rate = rate;

    const voices = synth.getVoices();
    const voice =
      voices.find((v) => v.lang === _lang || v.lang.replace("_", "-") === _lang) ||
      voices.find((v) => v.lang.startsWith(_lang.split("-")[0]));

    if (voice) utterance.voice = voice;

    utterance.onend = () => {
      if (isActive()) onNext?.();
    };

    utterance.onerror = () => {
      if (isActive()) onNext?.();
    };

    synth.speak(utterance);
  };

  const token = localStorage.getItem(KeyTokenName);
  const apiLang = _lang.startsWith("vi") ? "vi" : "en";
  const ttsUrl = getApiUrl(ENDPOINTS.ITEMS_TTS, { text: str, lang: apiLang });

  abortController = new AbortController();
  const audio = new Audio();

  fetch(ttsUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: abortController.signal,
  })
    .then((res) => {
      if (!isActive()) return null; // Lượt phát đã bị hủy
      if (!res.ok) throw new Error(`API Error: ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      if (!blob || !isActive()) return;

      const blobUrl = URL.createObjectURL(blob);
      audio.src = blobUrl;
      audio.playbackRate = Math.max(0.5, Math.min(rate, 4));

      audio.onended = () => {
        URL.revokeObjectURL(blobUrl);
        currentAudio = null;
        if (isActive()) onNext?.();
      };

      audio.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        currentAudio = null;
        if (isActive()) fallbackNative();
      };

      currentAudio = audio;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (isActive() && e.name !== "AbortError") {
            fallbackNative();
          }
        });
      }
    })
    .catch((err) => {
      // Chỉ chạy fallback nếu vẫn đang trong chính lượt hover này và không phải lỗi do chủ động Abort
      if (isActive() && err.name !== "AbortError") {
        console.warn("TTS API Fetch error, falling back to Native:", err.message);
        currentAudio = null;
        fallbackNative();
      }
    });
};

export const onPlayGoogleTTS = (str: string, onNext?: () => void, lang = "vi-VN", speed = 1) => {
  onPlayReadStr(str, onNext, lang, speed);
};

export interface IShadowingLoopOptions {
  text: string;
  speed?: number;
  lang?: string;
  pauseMs?: number;
  maxLoops?: number;
  onWordChange?: (wordIndex: number) => void;
  onLoopChange?: (loopNum: number) => void;
  onStop?: () => void;
}

export interface IShadowingLoopController {
  stop: () => void;
  isActive: () => boolean;
}

/**
 * Reusable helper for continuous shadowing repetition loop with word boundary tracking
 */
export const startShadowingLoop = (options: IShadowingLoopOptions): IShadowingLoopController => {
  let isRunning = true;
  let timer: any = null;

  const text = (options.text || "").trim();
  const speed = options.speed !== undefined ? options.speed : 1.0;
  const lang = options.lang || "en-US";
  const pauseMs = options.pauseMs !== undefined ? options.pauseMs : 1000;
  const maxLoops = options.maxLoops !== undefined ? options.maxLoops : 0;

  const targetTokens = text.split(/\s+/).filter(Boolean);
  const wordRanges: { start: number; end: number }[] = [];
  let cursor = 0;
  for (let i = 0; i < targetTokens.length; i++) {
    const tok = targetTokens[i];
    const foundStart = text.indexOf(tok, cursor);
    const start = foundStart !== -1 ? foundStart : cursor;
    const end = start + tok.length;
    wordRanges.push({ start, end });
    cursor = end;
  }

  const stop = () => {
    isRunning = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    options.onWordChange?.(-1);
    options.onStop?.();
  };

  const playIteration = (loopNum: number) => {
    if (!isRunning || typeof window === "undefined" || !window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = speed;

    const voices = window.speechSynthesis.getVoices();
    const enVoice =
      voices.find(
        (v) =>
          v.lang.startsWith("en") &&
          (v.name.includes("Samantha") ||
            v.name.includes("Alex") ||
            v.name.includes("Karen") ||
            v.name.includes("Enhanced"))
      ) ||
      voices.find((v) => v.lang.startsWith("en") && v.localService === true) ||
      voices.find((v) => v.lang === "en-US" || v.lang.startsWith("en"));
    if (enVoice) utterance.voice = enVoice;

    options.onLoopChange?.(loopNum);
    options.onWordChange?.(0);

    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      if (!isRunning || event.name !== "word") return;
      const charIdx = event.charIndex;
      for (let i = 0; i < wordRanges.length; i++) {
        const range = wordRanges[i];
        if (charIdx >= range.start - 2 && (i === wordRanges.length - 1 || charIdx < wordRanges[i + 1].start)) {
          options.onWordChange?.(i);
          break;
        }
      }
    };

    utterance.onend = () => {
      if (!isRunning) return;
      if (targetTokens.length > 0) {
        options.onWordChange?.(targetTokens.length - 1);
      }

      if (maxLoops > 0 && loopNum >= maxLoops) {
        stop();
        return;
      }

      const nextLoop = loopNum + 1;
      timer = setTimeout(() => {
        if (isRunning) {
          options.onWordChange?.(0);
          playIteration(nextLoop);
        }
      }, pauseMs);
    };

    utterance.onerror = () => {
      stop();
    };

    window.speechSynthesis.speak(utterance);
  };

  playIteration(1);

  return {
    stop,
    isActive: () => isRunning,
  };
};
